<template>
  <v-container>

    <v-dialog v-model="meetGuideVisible" persistent max-width="500px">
      <GoogleMeetGuide @finishedGuide="meetGuideVisible = false"/>
    </v-dialog>
    <v-row>
      <v-col cols="8">

        <v-card  outlined v-if="attempt">
          <v-card-title>
            {{ attempt.teachingSession.tutorial.subject.data.title }} > {{ attempt.teachingSession.tutorial.data.title }}
          </v-card-title>
          <v-card-subtitle>
            <span class="mr-3">{{ attempt.teachingSession.data.startTime | date('EEE do MMM yyyy, HH:mm') }} - {{ attempt.teachingSession.data.endTime | date('HH:mm') }}</span>
            <v-chip v-if="inProgress"
              color="green"
              text-color="white"
              small
              class="text-uppercase"
            >
              In progress
            </v-chip>
            <v-chip v-else-if="inGracePeriod"
              color="red"
              text-color="white"
              small
              class="text-uppercase"
            >
              Ending soon
            </v-chip>
            <v-chip v-else
              color="red"
              text-color="white"
              small
              class="text-uppercase"
            >
              Has ended
            </v-chip>
          </v-card-subtitle>

          <v-card-text class="mt-5">
            <div id="tutorial-html" class="student" v-html="attempt.teachingSession.tutorial.data.description"></div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4">

        <v-card outlined v-if="attempt">
          <v-card-title>Instructions</v-card-title>
          <v-card-text>
            <p>
              As you go through the tutorial click on whichever part you're currently working on. This will highlight it to you, and also show the lecturer your progress. They may offer you a Screenshare if they think you need help.
            </p>
            <p>
              If you're stuck on something you can also Request a Screenshare with the lecturer.
            </p>
          </v-card-text>
        </v-card>

        <v-card outlined v-if="attempt && (inProgress || inGracePeriod)" class="mt-5">
          <v-card-title>Screenshare</v-card-title>
          <v-card-text>
            <v-row>
              <v-col>
                <v-btn :disabled="attempt.data.screenshareStatus !== null" 
                  block
                  depressed 
                  class="primary" 
                  @click="updateAttempt('Requested screenshare', 'REQUESTED')">
                  Request a screenshare
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="attempt.data.screenshareStatus == 'REQUESTED'">
              <v-col class="text-center">
                Waiting for lecturer...
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-btn :disabled="attempt.data.screenshareStatus != 'OFFERED'" 
                  block 
                  depressed 
                  class="success" 
                  @click="joinScreenshare">
                  Join screenshare
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="attempt.data.screenshareStatus == 'STUDENT_JOINED' || attempt.data.screenshareStatus == 'LECTURER_JOINED'">
              <v-col class="text-center">
                In progress
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-btn :disabled="attempt.data.screenshareStatus != 'REQUESTED'  && attempt.data.screenshareStatus != 'OFFERED'" 
                  block 
                  depressed 
                  class="warning" 
                  @click="updateAttempt('Cancelled screenshare', null)">
                  Cancel screenshare
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import addMinutes from 'date-fns/addMinutes'
import firebase from '@/firebase.config'
import getCssSelector from 'css-selector-generator'
import GoogleMeetGuide from '@/components/GoogleMeetGuide'

export default {
  name: "Attempt",
  components: { GoogleMeetGuide },
  data: function() {
    return {
      meetGuideVisible: false,
      selectedElement: null,
      desktopNotified: false,
      screenshareWindow: null,
      progressListenerAdded: false
    }
  },
  methods: {
    async updateAttempt(message, screenshareStatus) {
      this.attempt.data.eventLog.push({
        timestamp: firebase.firestore.Timestamp.now(),
        creator: 'STUDENT',
        message: message
      })
      this.attempt.data.screenshareStatus = screenshareStatus
      
      await this.$store.dispatch('updateAttempt', this.attempt)
        
      // Report to GA
      firebase.analytics().logEvent('screenshare', {
        action: message
      })
    },
    async joinScreenshare() {

      await this.updateAttempt('Joined screenshare', 'STUDENT_JOINED')
      // Open the screenshare
      this.screenshareWindow = window.open(this.attempt.teachingSession.data.meetingLink, "_blank")

      // Cannot listen for the window closed event, because it is on a different domain
      // https://stackoverflow.com/questions/9388380/capture-the-close-event-of-popup-window-in-javascript/9388451
      let timer = setInterval(() => {
        if (this.screenshareWindow == null) {
          // Screenshare has been ended by the lecturer
          clearInterval(timer)
        }
        else if (this.screenshareWindow.closed) {
          // Screenshare has been ended by the student
          this.updateAttempt('Ended screenshare', null)
          this.screenshareWindow = null
          clearInterval(timer)
        }
      }, 1000)
    },
    click(event) {
      // if an element was previously selected then remove the
      // .highlightSelected class from it
      if (this.selectedElement !== null) {
        this.selectedElement.classList.remove('highlightSelected')
      }
      // get reference to the element user clicked on
      this.selectedElement = event.target

      // get unique CSS selector for that element
      const selector = getCssSelector(this.selectedElement, {
        root: document.querySelector('#tutorial-html')
      })

      // Add highlightSelected class
      this.selectedElement.classList.add('highlightSelected')

      // Update attempt with the selector
      this.attempt.data.currentCssSelector = selector
      this.$store.dispatch('updateAttempt', this.attempt)

      // Report to GA
      firebase.analytics().logEvent('update_progress')
    }
  },
  computed: {
    ...mapGetters({
      user: "user",
      studentSubjects: "studentSubjects"
    }),
    attempt() {
      let attempt = null
      // Search through Vuex
      this.studentSubjects.forEach(subject => {
        subject.tutorials && subject.tutorials.forEach(tutorial => {
          tutorial.teachingSessions && tutorial.teachingSessions.forEach(teachingSession => {
            teachingSession.attempts && teachingSession.attempts.forEach(atmpt => {
              if (atmpt.id === this.$route.params.id) {
                attempt = atmpt

                // If this is the first time the attempt has loaded add event listener
                if (!this.progressListenerAdded) {
                  Vue.nextTick().then(() => {
                    // Listen for student clicks within the tutorial HTML
                    document.querySelector('#tutorial-html').addEventListener('click', this.click)
                    this.progressListenerAdded = true
                  })
                }

                // If a screenshareWindow is open and the screenshareStatus is null, that means the
                // lecturer has ended the screenshare
                if (this.screenshareWindow && attempt.data.screenshareStatus == null) {
                  this.screenshareWindow.close()
                  this.screenshareWindow = null
                }  

                // Create desktop notifications if the lecturer has offered a screenshare
                if (attempt.data.screenshareStatus == 'OFFERED' && !this.desktopNotified) {
                  this.desktopNotified = true
                  
                  this.$notification.show('LESSONSQUARED NOTIFICATION', {
                    body: 'Lecturer has offered you a screenshare'
                  }, {})
                }
                else if (attempt.data.screenshareStatus != 'OFFERED' && this.desktopNotified) {
                  // Reset desktopNotified for the next time
                  this.desktopNotified = false
                }
              }
            })
          })
        })
      })
      return attempt
    },
    inProgress() {
      let inProgress = false
      if (this.attempt) {
        const now = new Date()
        inProgress = this.attempt.teachingSession.data.startTime < now && now < this.attempt.teachingSession.data.endTime
      }
      return inProgress
    },
    inGracePeriod() {
      let inGracePeriod = false
      if (this.attempt) {
        const now = new Date()
        const endTimewithGrace = addMinutes(this.attempt.teachingSession.data.endTime, 15)
        inGracePeriod = this.attempt.teachingSession.data.endTime < now && endTimewithGrace > now
      }
      return inGracePeriod
    }
  },
  mounted() {
    // Ask the user to enable desktop notifications
    this.$notification.requestPermission()

    // Report to GA
    firebase.analytics().logEvent('view_tutorial')
  }
}
</script>


<!-- Style cannot be scoped, as it does not affect v-html -->
<style>

#tutorial-html.student .highlightSelected {
  background-color: #f5f5f5;
  padding-top: 12px;
  padding-bottom: 12px;
  font-weight: bold;
}

#tutorial-html.student p:hover,
#tutorial-html.student li:hover,
#tutorial-html.student h1:hover,
#tutorial-html.student h2:hover,
#tutorial-html.student h3:hover {
  background-color: #f5f5f5;
  cursor:pointer;
}

#tutorial-html h1,
#tutorial-html h2,
#tutorial-html h3 {
  padding-top: 2px;
  padding-bottom: 3px;
}

#tutorial-html p {
  margin-bottom: 0px;
  padding-bottom: 16px;
}
</style>
